<template>
  <div class="era-naming-page">
    <div class="container">
      <!-- 面包屑导航 -->
      <BreadcrumbNav />
      <h1 class="page-title">Chinese Naming Styles Throughout History</h1>
      <p class="page-intro">
        Chinese naming styles have continuously evolved throughout history, with each era having its unique naming characteristics and popular trends.
        These naming customs deeply reflect the social values, aesthetic tastes, and cultural traditions of specific historical periods.
        By understanding the naming styles of different eras, we can better comprehend the changes and inheritance of Chinese culture.
      </p>

      <div class="timeline">
        <div class="timeline-item">
          <div class="timeline-point"></div>
          <div class="timeline-content">
            <h2>Pre-Qin to Han Dynasty (Circa 21st Century BC-220 AD)</h2>
            <div class="era-card">
              <div class="era-image">
                <div class="img-wrapper">
                  <img src="@/assets/images/bingmayong.png" alt="兵马俑时代" />
                </div>
              </div>
              <div class="era-details">
                <h3>Pre-Qin Period (Circa 21st Century BC-221 BC)</h3>
                <p><strong>Historical Background:</strong> Clan society gradually dissolved, slave-based states formed, and Zhou ritual systems began to establish.</p>
                <p><strong>Naming Characteristics:</strong></p>
                <ul>
                  <li>Predominantly single-character names with emphasis on resonant sounds</li>
                  <li>Names often related to bravery, moral virtue, and political aspirations</li>
                  <li>Separate use of clan names and surnames, with nobles having complex naming systems</li>
                  <li>Commoners typically used simple single names</li>
                </ul>
                <p><strong>Representative Names:</strong></p>
                <ul>
                  <li>伯夷 (Bo Yi), 叔齐 (Shu Qi) (ancient sages)</li>
                  <li>孔丘 (Kong Qiu), courtesy name 仲尼 (Zhongni) (Confucius)</li>
                  <li>嬴政 (Ying Zheng) (First Emperor of Qin)</li>
                  <li>吕不韦 (Lü Buwei) (merchant and politician)</li>
                </ul>

                <h3>Qin-Han Period (221 BC-220 AD)</h3>
                <p><strong>Historical Background:</strong> Unified empire established, imperial power strengthened, Confucianism began to become orthodox.</p>
                <p><strong>Naming Characteristics:</strong></p>
                <ul>
                  <li>Two-character names gradually increased</li>
                  <li>Emphasis on expressing virtue and aspirations</li>
                  <li>The "given name" and "courtesy name" system perfected, with men adopting courtesy names after coming of age at twenty</li>
                  <li>Name taboo systems began to form</li>
                </ul>
                <p><strong>Representative Names:</strong></p>
                <ul>
                  <li>刘邦 (Liu Bang), courtesy name 季 (Ji) (Emperor Gaozu of Han)</li>
                  <li>司马迁 (Sima Qian), courtesy name 子长 (Zichang) (author of "Records of the Grand Historian")</li>
                  <li>王昭君 (Wang Zhaojun) (one of the Four Beauties)</li>
                  <li>卫青 (Wei Qing), 霍去病 (Huo Qubing) (famous Han generals)</li>
                  <li>班固 (Ban Gu), courtesy name 孟坚 (Mengjian) (author of "Book of Han")</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div class="timeline-item">
          <div class="timeline-point"></div>
          <div class="timeline-content">
            <h2>Wei-Jin Southern and Northern Dynasties to Sui-Tang (220-907 AD)</h2>
            <div class="era-card">
              <div class="era-image">
                <div class="img-wrapper">
                  <img src="@/assets/images/weijinfengliu.png" alt="魏晋南北朝至隋唐时期" />
                </div>
              </div>
              <div class="era-details">
                <h3>Wei-Jin Southern and Northern Dynasties Period (220-589 AD)</h3>
                <p><strong>Historical Background:</strong> Political division, scholar-officials' prominence, Seven Sages of the Bamboo Grove, flourishing of metaphysics.</p>
                <p><strong>Naming Characteristics:</strong></p>
                <ul>
                  <li>Widespread use of courtesy names and literary names, emphasis on phonetic beauty</li>
                  <li>Admiration for elegant and profound meanings</li>
                  <li>Frequent use of particles like "之 (zhi)" and "若 (ruo)" in names</li>
                  <li>Names derived from natural scenery, such as wind, moon, bamboo, pine, etc.</li>
                </ul>
                <p><strong>Representative Names:</strong></p>
                <ul>
                  <li>嵇康 (Ji Kang), courtesy name 叔夜 (Shuye) (one of the Seven Sages)</li>
                  <li>阮籍 (Ruan Ji) (one of the Seven Sages)</li>
                  <li>陶渊明 (Tao Yuanming), courtesy name 元亮 (Yuanliang) (pastoral poet)</li>
                  <li>谢道韫 (Xie Daoyun) (talented woman)</li>
                  <li>王羲之 (Wang Xizhi), courtesy name 逸少 (Yishao) (calligrapher)</li>
                </ul>

                <h3>Sui-Tang Period (589-907 AD)</h3>
                <p><strong>Historical Background:</strong> Unified empire restored, ethnic integration, cultural prosperity, civil service examination system established.</p>
                <p><strong>Naming Characteristics:</strong></p>
                <ul>
                  <li>Names sought grandeur and magnificence</li>
                  <li>Frequent use of literary allusions and poetry in names</li>
                  <li>Emphasis on generational naming order, perfection of family naming systems</li>
                  <li>Multi-ethnic cultural integration led to more diverse names</li>
                </ul>
                <p><strong>Representative Names:</strong></p>
                <ul>
                  <li>李世民 (Li Shimin), courtesy name 叔明 (Shuming) (Emperor Taizong of Tang)</li>
                  <li>李白 (Li Bai), courtesy name 太白 (Taibai) (Immortal Poet)</li>
                  <li>杜甫 (Du Fu), courtesy name 子美 (Zimei) (Sage Poet)</li>
                  <li>王维 (Wang Wei), courtesy name 摩诘 (Mojie) (Buddha Poet)</li>
                  <li>武则天 (Wu Zetian) (China's only female emperor)</li>
                  <li>杨玉环 (Yang Yuhuan) (Imperial Consort)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div class="timeline-item">
          <div class="timeline-point"></div>
          <div class="timeline-content">
            <h2>Song, Yuan, Ming, and Qing Dynasties (960-1911 AD)</h2>
            <div class="era-card">
              <div class="era-image">
                <div class="img-wrapper">
                  <img src="@/assets/images/manjianghong.png" alt="宋元明清时期" />
                </div>
              </div>
              <div class="era-details">
                <h3>Song Dynasty (960-1279 AD)</h3>
                <p><strong>Historical Background:</strong> Rise of Neo-Confucianism, prosperous urban economy, emergence of literati and scholar-officials.</p>
                <p><strong>Naming Characteristics:</strong></p>
                <ul>
                  <li>Influence of Neo-Confucian thought, emphasis on moral qualities</li>
                  <li>Rigorous name structure with attention to symmetry</li>
                  <li>More refined generational naming systems within families</li>
                  <li>Admiration for simple and understated aesthetic styles</li>
                </ul>
                <p><strong>Representative Names:</strong></p>
                <ul>
                  <li>赵匡胤 (Zhao Kuangyin) (Emperor Taizu of Song)</li>
                  <li>苏轼 (Su Shi), courtesy name 子瞻 (Zizhan) (苏东坡 (Su Dongpo))</li>
                  <li>李清照 (Li Qingzhao) (female poet of Song)</li>
                  <li>朱熹 (Zhu Xi), courtesy name 元晦 (Yuanhui) (Neo-Confucian philosopher)</li>
                  <li>辛弃疾 (Xin Qiji), courtesy name 幼安 (You'an) (poet)</li>
                </ul>

                <h3>Yuan Dynasty (1271-1368 AD)</h3>
                <p><strong>Historical Background:</strong> Mongol rule, multi-ethnic integration, flourishing of Yuan drama.</p>
                <p><strong>Naming Characteristics:</strong></p>
                <ul>
                  <li>Integration of diverse cultures, Mongolian and Han names coexisted</li>
                  <li>Scholars' names reflected sentiments of a conquered nation</li>
                  <li>Dramatic literature flourished, names also carried theatrical elements</li>
                </ul>
                <p><strong>Representative Names:</strong></p>
                <ul>
                  <li>忽必烈 (Kublai Khan) (Emperor Shizu of Yuan)</li>
                  <li>郭守敬 (Guo Shoujing) (astronomer)</li>
                  <li>马致远 (Ma Zhiyuan) (Yuan drama writer)</li>
                  <li>王实甫 (Wang Shifu) (author of "Romance of the Western Chamber")</li>
                </ul>

                <h3>Ming Dynasty (1368-1644 AD)</h3>
                <p><strong>Historical Background:</strong> Han Chinese restoration, flourishing urban culture, developed novels and drama.</p>
                <p><strong>Naming Characteristics:</strong></p>
                <ul>
                  <li>Pursuit of elegance, reverence for antiquity</li>
                  <li>Emphasis on family genealogy, clear generational order</li>
                  <li>Scholars often adopted literary names, such as "青莲居士 (Qinglian Jushi)" or "板桥居士 (Banqiao Jushi)"</li>
                  <li>With commodity economy development, commoner names became more diverse</li>
                </ul>
                <p><strong>Representative Names:</strong></p>
                <ul>
                  <li>朱元璋 (Zhu Yuanzhang) (Emperor Taizu of Ming)</li>
                  <li>戚继光 (Qi Jiguang) (anti-piracy military commander)</li>
                  <li>唐伯虎 (Tang Bohu), courtesy name 子华 (Zihua) (painter)</li>
                  <li>李时珍 (Li Shizhen) (medical scientist)</li>
                  <li>汤显祖 (Tang Xianzu) (playwright)</li>
                </ul>

                <h3>Qing Dynasty (1644-1911 AD)</h3>
                <p><strong>Historical Background:</strong> Manchu rule, highly developed culture, increasing Western influence in later period.</p>
                <p><strong>Naming Characteristics:</strong></p>
                <ul>
                  <li>Integration of Manchu and Han cultures, Manchu names often included lineage indicators</li>
                  <li>Complete system of names and titles, scholars often had studio names, hall names, etc.</li>
                  <li>Complex and strict name taboo systems</li>
                  <li>Names reflecting reform thought appeared in late Qing</li>
                </ul>
                <p><strong>Representative Names:</strong></p>
                <ul>
                  <li>爱新觉罗·玄烨 (Aisin Gioro Xuanye) (Emperor Kangxi)</li>
                  <li>曹雪芹 (Cao Xueqin) (author of "Dream of the Red Chamber")</li>
                  <li>林则徐 (Lin Zexu) (anti-opium official)</li>
                  <li>梁启超 (Liang Qichao) (reform movement representative)</li>
                  <li>谭嗣同 (Tan Sitong) (one of the Six Gentlemen of the 1898 Reform)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div class="timeline-item">
          <div class="timeline-point"></div>
          <div class="timeline-content">
            <h2>Modern Era (1912-1978 AD)</h2>
            <div class="era-card">
              <div class="era-image">
                <div class="img-wrapper">
                  <img src="@/assets/images/tiananmen.png" alt="现代时期" />
                </div>
              </div>
              <div class="era-details">
                <h3>Republic of China Period (1912-1949 AD)</h3>
                <p><strong>Historical Background:</strong> Early establishment of democratic republic, May Fourth Movement, New Culture Movement.</p>
                <p><strong>Naming Characteristics:</strong></p>
                <ul>
                  <li>Patriotism and revolutionary spirit in names, such as "建国 (Jianguo)" (Building the Nation), "爱民 (Aimin)" (Loving the People)</li>
                  <li>Western thought influence, with terms like "民主 (Minzhu)" (Democracy), "科学 (Kexue)" (Science) appearing in names</li>
                  <li>Fusion of traditional culture and modern thought</li>
                  <li>Reduced use of courtesy names, simplified naming systems</li>
                </ul>
                <p><strong>Representative Names:</strong></p>
                <ul>
                  <li>孙中山 (Sun Zhongshan) (孙文 (Sun Wen), literary name 逸仙 (Yixian))</li>
                  <li>蔡元培 (Cai Yuanpei) (educator)</li>
                  <li>鲁迅 (Lu Xun) (周树人 (Zhou Shuren))</li>
                  <li>胡适 (Hu Shi) (New Culture Movement leader)</li>
                  <li>张学良 (Zhang Xueliang) (patriotic general)</li>
                  <li>宋庆龄 (Song Qingling), 宋美龄 (Song Meiling) (Song sisters)</li>
                </ul>

                <h3>Revolutionary War Period (1927-1949 AD)</h3>
                <p><strong>Historical Background:</strong> War of Resistance Against Japan, Liberation War, revolutionary struggle.</p>
                <p><strong>Naming Characteristics:</strong></p>
                <ul>
                  <li>Strong embodiment of revolutionary spirit and fighting will</li>
                  <li>Widespread use of party aliases (revolutionary pseudonyms)</li>
                  <li>Increase in names reflecting class standing</li>
                  <li>Women's names began to express independence and equality</li>
                </ul>
                <p><strong>Representative Names and Aliases:</strong></p>
                <ul>
                  <li>毛泽东 (Mao Zedong) (courtesy name 润之 (Runzhi))</li>
                  <li>周恩来 (Zhou Enlai)</li>
                  <li>朱德 (Zhu De)</li>
                  <li>刘少奇 (Liu Shaoqi)</li>
                  <li>邓小平 (Deng Xiaoping) (original name 邓先圣 (Deng Xiansheng))</li>
                  <li>江姐 (Jiang Jie) (江竹筠 (Jiang Zhuyun))</li>
                </ul>

                <h3>Early PRC to Pre-Cultural Revolution (1949-1966 AD)</h3>
                <p><strong>Historical Background:</strong> New China established, post-war reconstruction, socialist construction.</p>
                <p><strong>Naming Characteristics:</strong></p>
                <ul>
                  <li>Names like "建国 (Jianguo)," "建设 (Jianshe)" (Construction), "建华 (Jianhua)" appeared in large numbers</li>
                  <li>Names reflecting industrial and agricultural development became popular, such as "铁强 (Tieqiang)" (Iron Strong), "钢 (Gang)" (Steel)</li>
                  <li>Names embodying international friendship, like "友谊 (Youyi)" (Friendship), "和平 (Heping)" (Peace)</li>
                  <li>Combination of revolutionary tradition and national pride</li>
                </ul>
                <p><strong>Common Popular Names:</strong></p>
                <ul>
                  <li>建国 (Jianguo), 建华 (Jianhua), 建军 (Jianjun)</li>
                  <li>卫东 (Weidong), 卫红 (Weihong)</li>
                  <li>志强 (Zhiqiang), 志刚 (Zhigang)</li>
                  <li>红梅 (Hongmei), 红军 (Hongjun)</li>
                  <li>跃进 (Yuejin), 卫国 (Weiguo)</li>
                </ul>

                <h3>Cultural Revolution Period (1966-1976 AD)</h3>
                <p><strong>Historical Background:</strong> Cultural Revolution, frequent political movements, intensified ideology.</p>
                <p><strong>Naming Characteristics:</strong></p>
                <ul>
                  <li>Emergence of highly politicized names</li>
                  <li>Widespread use of revolutionary terminology</li>
                  <li>Strong revolutionary heroism</li>
                  <li>Embodiment of collectivist spirit</li>
                </ul>
                <p><strong>Common Popular Names:</strong></p>
                <ul>
                  <li>卫东 (Weidong), 卫红 (Weihong), 卫国 (Weiguo)</li>
                  <li>向阳 (Xiangyang), 向红 (Xianghong)</li>
                  <li>东方红 (Dongfanghong)</li>
                  <li>跃进 (Yuejin), 进步 (Jinbu)</li>
                  <li>铁军 (Tiejun), 铁梅 (Tiemei)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div class="timeline-item">
          <div class="timeline-point"></div>
          <div class="timeline-content">
            <h2>Reform and Opening Up Era (1978 to Present)</h2>
            <div class="era-card">
              <div class="era-image">
                <div class="img-wrapper">
                  <img src="@/assets/images/feichuan.png" alt="改革开放时期" />
                </div>
              </div>
              <div class="era-details">
                <h3>1980s</h3>
                <p><strong>Historical Background:</strong> Early reform and opening up, liberation of thought, return to traditional culture.</p>
                <p><strong>Naming Characteristics:</strong></p>
                <ul>
                  <li>Pursuit of cultural connotation, incorporation of classical poetry into names</li>
                  <li>Emphasis on personal ideals and aspirations</li>
                  <li>Importance of phonetic beauty</li>
                  <li>Popularity of characters like "伟 (Wei)" (Great), "强 (Qiang)" (Strong), "勇 (Yong)" (Brave) conveying positive advancement</li>
                </ul>
                <p><strong>Common Popular Names:</strong></p>
                <ul>
                  <li>伟明 (Weiming), 伟华 (Weihua)</li>
                  <li>文静 (Wenjing), 文慧 (Wenhui)</li>
                  <li>亚男 (Yanan), 雅楠 (Yanan)</li>
                  <li>小平 (Xiaoping), 小军 (Xiaojun)</li>
                  <li>婷婷 (Tingting), 芳芳 (Fangfang)</li>
                </ul>

                <h3>1990s</h3>
                <p><strong>Historical Background:</strong> Market economy development, Hong Kong and Taiwan cultural influence, increased international exchange.</p>
                <p><strong>Naming Characteristics:</strong></p>
                <ul>
                  <li>Emphasis on aesthetic value and cultural taste</li>
                  <li>Increased use of unique and rare characters</li>
                  <li>Double-character structure names mainstream</li>
                  <li>Hong Kong and Taiwan celebrity influence, with widespread use of characters like "佳 (Jia)" and "俊 (Jun)"</li>
                </ul>
                <p><strong>Common Popular Names:</strong></p>
                <ul>
                  <li>俊杰 (Junjie), 俊哲 (Junzhe)</li>
                  <li>佳欣 (Jiaxin), 佳怡 (Jiayi)</li>
                  <li>梦婷 (Mengting), 梦琪 (Mengqi)</li>
                  <li>志远 (Zhiyuan), 志强 (Zhiqiang)</li>
                  <li>雪莲 (Xuelian), 雪梅 (Xuemei)</li>
                </ul>

                <h3>2000s</h3>
                <p><strong>Historical Background:</strong> Internet rise, deepening globalization, educational emphasis.</p>
                <p><strong>Naming Characteristics:</strong></p>
                <ul>
                  <li>Emphasis on traditional cultural elements, such as classical characters like "轩 (Xuan)" and "宸 (Chen)"</li>
                  <li>Focus on the uniqueness and distinctiveness of names</li>
                  <li>Harmony of tone and rhythm, avoiding obscure and difficult-to-read characters</li>
                  <li>Reflection of social competitiveness, popularity of characters like "睿 (Rui)" (Wise), "智 (Zhi)" (Intelligent), "博 (Bo)" (Extensive)</li>
                </ul>
                <p><strong>Common Popular Names:</strong></p>
                <ul>
                  <li>浩然 (Haoran), 浩宇 (Haoyu)</li>
                  <li>子轩 (Zixuan), 紫轩 (Zixuan)</li>
                  <li>子涵 (Zihan), 梓涵 (Zihan)</li>
                  <li>欣怡 (Xinyi), 欣然 (Xinran)</li>
                  <li>博文 (Bowen), 博宇 (Boyu)</li>
                </ul>

                <h3>2010s to Present</h3>
                <p><strong>Historical Background:</strong> Increased cultural confidence, traditional culture revival, expanded international perspective.</p>
                <p><strong>Naming Characteristics:</strong></p>
                <ul>
                  <li>Deep revival of traditional culture, classical elegance becomes mainstream</li>
                  <li>Emphasis on auspicious meanings and cultural connotations of characters</li>
                  <li>Consideration of international perspective, accounting for Chinese-English conversion</li>
                  <li>Balance between uniqueness and aesthetic value</li>
                </ul>
                <p><strong>Common Popular Names:</strong></p>
                <ul>
                  <li>宸宇 (Chenyu), 宸熙 (Chenxi)</li>
                  <li>子涵 (Zihan), 子轩 (Zixuan)</li>
                  <li>沐恩 (Muen), 沐阳 (Muyang)</li>
                  <li>芷若 (Zhiruo), 芷晴 (Zhiqing)</li>
                  <li>圣瑞 (Shengrui), 圣轩 (Shengxuan)</li>
                  <li>一辰 (Yichen), 依然 (Yiran)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="conclusion">
        <h2>The Generational Imprint of Names</h2>
        <p>
          Each era's naming style reflects the social values and cultural trends of its specific historical period. From the idealism of the revolutionary era
          to the enterprising spirit of the reform and opening up, to the current return to and innovation of traditional culture, names serve as mirrors of changing times.
          As society develops, Chinese people continue to create new names that align with contemporary characteristics while drawing from traditional culture,
          demonstrating the vitality and creativity of Chinese culture.
        </p>
        <p>
          Looking at the long river of Chinese naming history, we can see the rich cultural connotations and zeitgeist it contains. Names are not only symbols of personal identity
          but also microcosms of their eras. Understanding these historical naming styles helps us more comprehensively recognize the diversity and evolution of Chinese culture,
          while also providing rich reference and inspiration for modern naming practices.
        </p>
      </div>

          <!-- Explore More 组件 -->
    <ExploreMore :currentPath="$route.path" />
    </div>
    

  </div>
</template>

<script>
import BreadcrumbNav from '@/components/BreadcrumbNav.vue';
import ExploreMore from '@/components/ExploreMore.vue';

export default {
  name: 'EraNamingStyles',
  components: {
    BreadcrumbNav,
    ExploreMore
  },
  mounted() {
    window.scrollTo(0, 0);
  }
}
</script>

<style scoped>
.era-naming-page {
  padding-top: 70px;
  padding-bottom: 50px;
  background-color: #f8f9fa;
  min-height: 100vh;
}

.container {
  max-width: 960px;
  margin: 0 auto;
  padding: 0 20px;
}

.page-title {
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 20px;
  color: #333;
}

.page-intro {
  font-size: 1.1rem;
  line-height: 1.8;
  text-align: center;
  margin: 0 auto 50px;
  max-width: 800px;
  color: #666;
}

.timeline {
  position: relative;
  margin: 30px 0 0;
  padding: 0;
}

.timeline::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 25px;
  width: 3px;
  background: #e60012;
  z-index: 1;
}

.timeline-item {
  position: relative;
  margin-bottom: 50px;
}

.timeline-point {
  position: absolute;
  top: 10px;
  left: 25px;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background: #e60012;
  transform: translateX(-50%);
  z-index: 2;
  box-shadow: 0 0 0 3px rgba(230, 0, 18, 0.2);
}

.timeline-content {
  margin-left: 50px;
  background: transparent;
  position: relative;
}

.timeline-content h2 {
  font-size: 1.6rem;
  margin-bottom: 20px;
  color: #333;
  border-bottom: 2px solid #e60012;
  padding-bottom: 10px;
  display: inline-block;
}

.era-card {
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
  display: flex;
  flex-direction: column;
}

.era-image {
  background-color: #f9f9f9;
  height: auto;
  overflow: hidden;
  padding: 0;
  margin: 0;
  width: 100%;
}

.img-wrapper {
  width: 100%;
  height: 340px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
}

.img-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transition: transform 0.3s ease;
}

.era-card:hover .img-wrapper img {
  transform: scale(1.03);
}

.era-details {
  padding: 25px;
}

.era-details h3 {
  font-size: 1.4rem;
  color: #333;
  margin: 20px 0 15px;
}

.era-details h3:first-child {
  margin-top: 0;
}

.era-details p {
  margin-bottom: 15px;
  line-height: 1.7;
  color: #444;
}

.era-details ul {
  margin-bottom: 20px;
  padding-left: 20px;
}

.era-details li {
  margin-bottom: 8px;
  line-height: 1.6;
}

.conclusion {
  margin-top: 40px;
  padding: 25px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.conclusion h2 {
  font-size: 1.6rem;
  color: #333;
  margin-bottom: 20px;
  text-align: center;
}

.conclusion p {
  line-height: 1.8;
  margin-bottom: 15px;
  color: #444;
}

@media (max-width: 768px) {
  .timeline::before {
    left: 20px;
  }
  
  .timeline-point {
    left: 20px;
  }
  
  .timeline-content {
    margin-left: 40px;
  }
  
  .img-wrapper {
    height: 220px;
  }
  
  .page-title {
    font-size: 2rem;
  }
  
  .timeline-content h2 {
    font-size: 1.4rem;
  }
  
  .era-details h3 {
    font-size: 1.3rem;
  }
  
  .container {
    padding: 0 15px;
  }
}
</style> 